<template>
    <Head title="Orders"/>
    <list-page-template
        name="Requests"
        ref="listTemplate"
        route="orders.index"
        :global-search="true"
        :filters="filters" v-model:sort="currentSort" :sort-options="sortOptions" v-model:active-filters="activeFilters"
        description="Manage current and past requests">
        <h2 id="products-heading" class="sr-only">Requests</h2>
        <div v-if="statsWithCount" class="pb-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
            <div v-for="(stat, val) in statsWithCount"
                 @click="applyFilter(val, stat.label)"
                 class="cursor-pointer flex flex-row items-center px-4 py-6 mr-4 mb-2 bg-white dark:bg-gray-700 dark:text-white shadow rounded-lg">
                <span class="flex-grow">{{ stat.label }}</span>
                <span>{{ stat.count }}</span>
            </div>
        </div>
        <div class="flex flex-col">
            <div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                <div class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                    <div class="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                        <table class="min-w-full divide-y divide-gray-200">
                            <thead class="bg-gray-50">
                            <tr>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reference</th>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Channels</th>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Images</th>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time</th>
                            </tr>
                            </thead>
                            <tbody class="bg-white divide-y divide-gray-200">
                            <Link v-for="request in pagination.data" class="cursor-pointer" :key="request.id" method="get" :href="this.$route('orders.show', request.reference)" as="tr">
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {{ request.reference }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    <div class="flex flex-col">
                                        <span>{{ request.customer.name }}</span>
                                        <div v-for="customerChannel in request.customer.channels.filter(c => c.active && c.is_primary)">
                                            <span class="text-xs text-gray-400 mr-2">{{ customerChannel.value }}</span>
                                        </div>
                                    </div>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <span v-if="!request.channels.length" class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">-</span>
                                    <div v-for="channel in request.channels">
                                        <span class="text-sm text-gray-500 mr-2">{{ channel.value }}</span>
                                        <span class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">{{ channel.meta.name }}</span>
                                    </div>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    <div class="flex overflow-hidden -space-x-1">
                                        <div v-if="!request.images.length" class="inline-block h-8 w-8 rounded-full ring-2 ring-white bg-red-100"/>
                                        <template v-for="image in request.images" :key="image.id">
                                            <img v-if="image.imageUrl" class="object-cover inline-block h-8 w-8 rounded-full ring-2 ring-white bg-gray-200" :src="image.imageUrl" :alt="image.id" />
                                            <div v-else class="object-cover inline-block h-8 w-8 rounded-full ring-2 ring-white bg-gray-200" />
                                        </template>
                                    </div>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    <span class="flex flex-row items-center">
                                        <component :class="`text-${status[request.status].color}-500`" class="h-4 w-4 mr-1.5" :is="status[request.status].icon"></component>
                                        {{ status[request.status].label }}
                                    </span>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {{ beautifiedTime(request.created_at) }}
                                </td>
                            </Link>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
        <pagination :pagination="this.pagination" />
    </list-page-template>
</template>

<script>
import Layout from '@/Shared/Layout'
import { isProxy, toRaw } from "vue"
import Pagination from '@/Shared/Pagination'
import {Head, Link} from "@inertiajs/inertia-vue3";
import ListPageTemplate from "@/Shared/PageSegments/ListPageTemplate";
import {
    BanIcon,
    GiftIcon,
    SparklesIcon,
    BadgeCheckIcon,
    CheckCircleIcon,
    ExclamationIcon,
    LightningBoltIcon,
    ExclamationCircleIcon,
    PresentationChartLineIcon,
} from "@heroicons/vue/solid";
import {Inertia} from "@inertiajs/inertia";
import {beautifiedTime} from "@/Utils/functions";

export default {
    layout: Layout,
    props: {
        auth: Object,
        sort: String,
        stats: Object,
        filters: Object,
        pagination: Object,
        sortOptions: Object,
        filterOptions: Object,
    },
    components: {
        Head,
        Link,
        Pagination,
        ListPageTemplate,
    },
    data() {
        return {
            currentSort: this.sort ?? '',
            activeFilters: []
        }
    },
    computed: {
        statsWithCount() {
            return Object.filter(this.stats, c => c.count > 0)
        }
    },
    setup(props) {
        return {
            sortOptions: isProxy(props.sortOptions) ? toRaw(props.sortOptions) : props.sortOptions,
            filters: isProxy(props.filterOptions) ? toRaw(props.filterOptions) : props.filterOptions,

            status: {
                "": {color: "zinc", label: "-", icon: BanIcon},
                "new": {color: "cyan", label: "New", icon: SparklesIcon},
                "on_hold": {color: "orange", label: "On Hold", icon: ExclamationCircleIcon},
                "processing": {color: "indigo", label: "Processing", icon: PresentationChartLineIcon},
                "ready": {color: "blue", label: "Ready", icon: GiftIcon},
                "sending_to_channels": {color: "pink", label: "Sending", icon: LightningBoltIcon},
                "sending_failed": {color: "red", label: "Sending Failed", icon: ExclamationIcon},
                "sending_partially_failed": {color: "orange", label: "Sending Partially Failed", icon: ExclamationIcon},
                "sent": {color: "green", label: "Delivered", icon: CheckCircleIcon},
                "completed_without_sending": {color: "green", label: "Completed", icon: BadgeCheckIcon},
            },
        }
    },
    mounted() {
        this.$echo.private('new_request').listen('.created', message => this.reloadPage());
    },
    beforeUnmount() {
        this.$echo.leave(`new_request`)
    },
    methods: {
        beautifiedTime,
        applyFilter(status, label) {
            if (this.activeFilters.filter(f => f.value === status).length) {
                Array.removeWhere(this.activeFilters, f => f.value === status);
            } else {
                this.activeFilters = [{
                    value: status,
                    valueLabel: label,
                    name: "status",
                    label: "State",
                }];
            }
            this.$refs.listTemplate.makeRequest(this.activeFilters)
        },
        reloadPage() {
            Inertia.reload({
                preserveScroll: true
            })
        }
    }
}
</script>
